/**
 * État de l'application et actions — couche de commande de l'interface.
 *
 * Le domaine est `readonly` de bout en bout : chaque action produit un nouveau
 * document et réutilise le reste, ce qui rend la détection de changement par
 * identité suffisante.
 */

import { computed, signal, type ReadonlySignal, type Signal } from '@preact/signals';
import type { CategoryKey } from '../core/categories';
import { describeError, DomainError } from '../core/errors';
import { newId } from '../core/ids';
import {
  nowLocalDateTime,
  nowRfc3339,
  type LocalDateTime,
} from '../core/local-datetime';
import type {
  Passage,
  Poi,
  PoiId,
  ProgressionInterval,
  RaceState,
  Results,
  RoadbookDocument,
  SearchIntegrity,
  SearchParameterSet,
} from '../core/model';
import { EMPTY_RESULTS } from '../core/model';
import {
  defaultParameters,
  validateParameters,
  type DisplayParameters,
  type Parameters,
} from '../core/parameters';
import type { Coordinate } from '../geo/geodesy';
import { computePassages, passageAtProgression, type PassageComputation } from '../geo/passages';
import { simplifyTrack } from '../geo/simplify';
import { buildSpatialIndex, type SpatialIndex } from '../geo/spatial-index';
import { coordinateAtProgression, type ComputedTrack } from '../geo/track';
import { buildRoute, type RouteImport, type SelectableElement } from '../gpx/import';
import { parseGpxFile, type GpxDocument } from '../gpx/parser';
import { parseParametersFile, serializeParametersFile } from '../io/parameters';
import {
  MAX_ROADBOOK_BYTES,
  parseRoadbookFile,
  proposedFileName,
  serializeRoadbook,
} from '../io/roadbook';
import { DEFAULT_ENDPOINT_ID, endpointById, isEndpointId, type EndpointId } from '../overpass/endpoints';
import { countAutomaticPois, RESULT_CAP } from '../overpass/merge';
import { OverpassClient } from '../overpass/client';
import { buildChunks, estimateSearch, type SearchChunk, type SearchEstimate } from '../overpass/query';
import { initialSearchState, mergeIntervals, runSearch, subtractIntervals, type SearchProgress, type SearchState } from '../overpass/search';
import { deliverFile, pickFile, type PreparedExport } from '../platform/lifecycle';
import { isOffline, OfflineManager } from '../platform/offline';
import { readPersistenceStatus, requestPersistence, type PersistenceStatus } from '../platform/persistence';
import { computeRoadbook, ridingCurveNeedsRebuild } from '../roadbook/compute';
import { buildDisplayRows, type DisplayRow } from '../roadbook/display';
import { computeFreshness, trackFingerprint } from '../roadbook/freshness';
import { buildRidingTimeCurve, type RidingTimeCurve } from '../roadbook/riding-time';
import { requiresSleepPrompt } from '../roadbook/schedule';
import { SaveScheduler, SessionLock, SessionStore } from '../store/session';
import { ERRORS } from './labels';

export const APP_VERSION = '1.0.0';

/**
 * Inventaire fermé de EF-NAV-001 : deux onglets permanents et le mode course.
 * `setup` et `roadbook` sont atteignables en un geste l'un depuis l'autre ;
 * `race` masque la barre d'onglets.
 */
export type ScreenName = 'setup' | 'roadbook' | 'race';

/**
 * Surfaces temporaires de EF-NAV-001. Elles se superposent à l'écran courant,
 * ne changent pas l'onglet actif et se referment sans quitter le contexte.
 */
export type SheetName = 'actions' | 'goto' | 'recalc' | 'filters' | 'manualPoi' | 'purge' | 'raceActions';

export type LongOperation =
  | 'importation d’un GPX'
  | 'importation d’un roadbook'
  | 'importation de paramètres'
  | 'recherche de POI'
  | 'calcul du roadbook'
  | 'purge des POI';

export interface Message {
  readonly id: string;
  readonly kind: 'info' | 'warning' | 'error';
  readonly text: string;
}

export interface SessionInfo {
  readonly title: string;
  readonly savedAt: string;
  readonly hasInterruptedSearch: boolean;
}

/** Confirmation en attente d'une décision explicite de l'utilisateur. */
export interface PendingConfirmation {
  readonly title: string;
  readonly body: string;
  readonly detail?: readonly string[];
  readonly confirmLabel: string;
  readonly offerExport: boolean;
  readonly onConfirm: () => void | Promise<void>;
}

export interface TrackSelection {
  readonly document: GpxDocument;
  readonly elements: readonly SelectableElement[];
  readonly fileName: string;
}

export interface RaceUiState {
  readonly active: boolean;
  readonly locked: boolean;
  readonly positionProblem: string | null;
  readonly offRoute: boolean;
  readonly awaitingChoice: readonly { progressionMeters: number; distanceToRouteMeters: number }[];
  readonly pendingConfirmationOfJump: boolean;
  readonly lastComputedAtLocal: LocalDateTime | null;
}

// ---------------------------------------------------------------------------
// Signaux
// ---------------------------------------------------------------------------

export const screen: Signal<ScreenName> = signal<ScreenName>('roadbook');
/** Feuille ouverte, le cas échéant. Une seule à la fois. */
export const sheet: Signal<SheetName | null> = signal<SheetName | null>(null);
export const document_: Signal<RoadbookDocument | null> = signal<RoadbookDocument | null>(null);
export const parameters: Signal<Parameters> = signal<Parameters>(defaultParameters(nowLocalDateTime()));
export const endpointId: Signal<EndpointId> = signal<EndpointId>(DEFAULT_ENDPOINT_ID);
export const busy: Signal<LongOperation | null> = signal<LongOperation | null>(null);
export const readOnly: Signal<boolean> = signal(false);
export const messages: Signal<readonly Message[]> = signal<readonly Message[]>([]);
export const sessionInfo: Signal<SessionInfo | null> = signal<SessionInfo | null>(null);
export const pendingConfirmation: Signal<PendingConfirmation | null> = signal<PendingConfirmation | null>(null);
export const trackSelection: Signal<TrackSelection | null> = signal<TrackSelection | null>(null);
export const searchProgress: Signal<SearchProgress | null> = signal<SearchProgress | null>(null);
export const searchEstimate: Signal<SearchEstimate | null> = signal<SearchEstimate | null>(null);
export const persistedSearchState: Signal<SearchState | null> = signal<SearchState | null>(null);
export const persistence: Signal<PersistenceStatus> = signal<PersistenceStatus>('indéterminée');
export const offlineReport = signal(new OfflineManager().current);
export const updateState = signal(new OfflineManager().updateState);
/** POI dont la ligne doit être ramenée dans la zone visible au retour sur le roadbook. */
export const roadbookReturnPoiId: Signal<PoiId | null> = signal<PoiId | null>(null);
/**
 * Lignes dont le détail est déplié, désignées par la clé stable de leur ligne.
 * Le dépliant remplace l'écran Détail : la liste n'est jamais quittée, donc sa
 * position est conservée par construction (EF-RBK-002, EF-NAV-007).
 */
export const expandedRows: Signal<ReadonlySet<string>> = signal<ReadonlySet<string>>(new Set());
/**
 * Identifiants des sections repliables actuellement ouvertes. La valeur
 * initiale fixe les sections ouvertes par défaut — seule source de vérité,
 * pour éviter toute divergence avec un défaut porté par le composant.
 * Persiste l'état plié/déplié au-delà du démontage de l'écran qui les
 * contient (changement d'onglet, EF-NAV-007).
 */
export const openDisclosures: Signal<ReadonlySet<string>> = signal<ReadonlySet<string>>(
  new Set(['setup-parcours', 'setup-parametres']),
);
/**
 * Position de défilement de l'onglet Roadbook, conservée entre deux
 * activations comme l'exige EF-NAV-007.
 */
export const roadbookScrollTop: Signal<number> = signal(0);
/**
 * Demande de positionnement de la liste, émise par la feuille « Aller à un
 * point ». Le jeton change à chaque demande, ce qui permet de redemander la
 * même ligne après que l'utilisateur a fait défiler la liste lui-même.
 */
export const listScrollRequest: Signal<{ readonly index: number; readonly token: number } | null> =
  signal<{ readonly index: number; readonly token: number } | null>(null);
export const editingPoiId: Signal<PoiId | null> = signal<PoiId | null>(null);
export const purgeUndoCount: Signal<number> = signal(0);
export const lastExport: Signal<PreparedExport | null> = signal<PreparedExport | null>(null);
export const storageUnavailable: Signal<boolean> = signal(false);
export const raceState: Signal<RaceUiState> = signal<RaceUiState>({
  active: false,
  locked: false,
  positionProblem: null,
  offRoute: false,
  awaitingChoice: [],
  pendingConfirmationOfJump: false,
  lastComputedAtLocal: null,
});

let store: SessionStore | null = null;
let lock: SessionLock | null = null;
let saver: SaveScheduler | null = null;
let searchAbort: AbortController | null = null;
let suspendSearch = false;

const offlineManager = new OfflineManager();

// ---------------------------------------------------------------------------
// Valeurs dérivées
// ---------------------------------------------------------------------------

let indexCache: { track: ComputedTrack; index: SpatialIndex } | null = null;

export function spatialIndex(): SpatialIndex | null {
  const track = document_.value?.route.track ?? null;
  if (track === null) return null;
  if (indexCache?.track !== track) indexCache = { track, index: buildSpatialIndex(track) };
  return indexCache.index;
}

let curveCache: { track: ComputedTrack; speed: Parameters['speed']; curve: RidingTimeCurve } | null = null;

/**
 * La courbe `T` n'est reconstruite que lorsque la trace de calcul ou les quatre
 * vitesses changent (EF-CAL-005, EF-CAL-009).
 */
export function ridingCurve(): RidingTimeCurve | null {
  const track = document_.value?.route.track ?? null;
  if (track === null) return null;
  const speed = parameters.value.speed;
  const cached = curveCache;
  const rebuild = cached === null || ridingCurveNeedsRebuild(cached.speed, speed, cached.track !== track);
  if (!rebuild && cached !== null) return cached.curve;

  const rebuilt = { track, speed, curve: buildRidingTimeCurve(track, speed) };
  curveCache = rebuilt;
  return rebuilt.curve;
}

let fingerprintCache: { track: ComputedTrack; value: string } | null = null;

function routeFingerprint(track: ComputedTrack): string {
  if (fingerprintCache?.track !== track) fingerprintCache = { track, value: trackFingerprint(track) };
  return fingerprintCache.value;
}

/** Marqueur « valeurs périmées » de EF-CAL-005, réévalué à chaque affichage. */
export const isStale: ReadonlySignal<boolean> = computed(() => {
  const current = document_.value;
  if (current === null) return false;
  const recorded = current.results.freshness;
  if (recorded === null) return false;
  const now = computeFreshness({
    routeFingerprint: routeFingerprint(current.route.track),
    pois: current.pois,
    parameters: { speed: parameters.value.speed, schedule: parameters.value.schedule },
  });
  return now !== recorded;
});

export const hasResult: ReadonlySignal<boolean> = computed(
  () => document_.value?.results.totals !== null && document_.value?.results.totals !== undefined,
);

export const displayRows: ReadonlySignal<readonly DisplayRow[]> = computed(() => {
  const current = document_.value;
  if (current === null) return [];
  return buildDisplayRows({
    pois: current.pois,
    stops: current.results.stops,
    missingRanges: current.results.searchIntegrity.missingRanges,
    display: parameters.value.display,
    routeLengthMeters: current.route.track.lengthMeters,
  });
});

export const automaticPoiCount: ReadonlySignal<number> = computed(() =>
  document_.value === null ? 0 : countAutomaticPois(document_.value.pois),
);

/**
 * Notice de sommeil recalculée automatiquement (EF-CAL-…) : dépend de la
 * courbe de vitesse (trace, vitesses) et de l'horaire. Remplace l'ancien
 * déclenchement manuel par bouton.
 */
export const sleepPromptNeeded: ReadonlySignal<boolean> = computed(() => {
  const curve = ridingCurve();
  if (curve === null) return false;
  return requiresSleepPrompt(curve, parameters.value.schedule);
});

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

/** Ouvre une feuille. Toute autre feuille ouverte est refermée. */
export function openSheet(name: SheetName): void {
  sheet.value = name;
}

export function closeSheet(): void {
  sheet.value = null;
}

/**
 * Bascule le dépliant d'une ligne. Plusieurs lignes peuvent rester dépliées :
 * comparer deux ravitaillements voisins est un usage courant.
 */
export function toggleRowExpanded(key: string): void {
  const next = new Set(expandedRows.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  expandedRows.value = next;
}

/** Bascule le plié/déplié d'une section (`Disclosure`) désignée par son id. */
export function toggleDisclosure(id: string): void {
  const next = new Set(openDisclosures.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  openDisclosures.value = next;
}

/** Amène la liste sur une ligne et referme la feuille qui l'a demandée. */
export function goToRow(index: number): void {
  const previous = listScrollRequest.peek();
  listScrollRequest.value = { index, token: (previous?.token ?? 0) + 1 };
  screen.value = 'roadbook';
  closeSheet();
}

export function notify(kind: Message['kind'], text: string): void {
  messages.value = [...messages.value, { id: newId(), kind, text }];
}

export function dismissMessage(id: string): void {
  messages.value = messages.value.filter((message) => message.id !== id);
}

export function clearMessages(): void {
  messages.value = [];
}

/**
 * Au plus une opération longue peut être active à un instant donné. Toute
 * demande d'une seconde est refusée par un message nommant l'opération en
 * cours, sans interrompre l'opération active (EF-NAV-005).
 */
function claimOperation(operation: LongOperation): boolean {
  if (readOnly.value) {
    notify('error', ERRORS.readOnlyContext);
    return false;
  }
  const current = busy.value;
  if (current !== null) {
    notify('error', ERRORS.busy(current, 'Annuler'));
    return false;
  }
  busy.value = operation;
  // Toute opération longue ferme la fenêtre d'annulation de la purge.
  void closePurgeWindow();
  return true;
}

function releaseOperation(): void {
  busy.value = null;
}

// ---------------------------------------------------------------------------
// Cycle de vie
// ---------------------------------------------------------------------------

export async function initialize(): Promise<void> {
  persistence.value = await readPersistenceStatus();

  offlineManager.onReport((report) => {
    offlineReport.value = report;
  });
  offlineManager.onUpdate((state) => {
    updateState.value = state;
  });
  void offlineManager.register();

  try {
    store = await SessionStore.open();
  } catch (error) {
    storageUnavailable.value = true;
    notify('error', describeError(error));
    return;
  }

  lock = new SessionLock(store);
  const acquired = await lock.acquire();
  readOnly.value = !acquired;
  if (acquired) {
    lock.start(() => {
      readOnly.value = true;
      notify('warning', ERRORS.readOnlyContext);
    });
  }

  const storedEndpoint = await store.readSetting<string>('endpoint');
  if (isEndpointId(storedEndpoint)) endpointId.value = storedEndpoint;

  const storedExport = await store.readSetting<PreparedExport>('lastExport');
  if (storedExport !== null) lastExport.value = storedExport;

  const storedParameters = await store.readSetting<Parameters>('parameters');
  if (storedParameters !== null) parameters.value = storedParameters;

  // Une purge dont la fenêtre était ouverte à la destruction du contexte est
  // définitive : la reprise ne propose jamais de l'annuler.
  await store.commitPurge();

  const snapshot = await store.load();
  if (snapshot !== null) {
    persistedSearchState.value = snapshot.searchState;
    sessionInfo.value = {
      title: snapshot.document.title,
      savedAt: snapshot.savedAt,
      hasInterruptedSearch:
        snapshot.searchState !== null && snapshot.searchState.pendingChunkIndices.length > 0,
    };
  }

  saver = new SaveScheduler(
    store,
    () => ({
      document: document_.value ?? emptyDocument(),
      parameters: parameters.value,
    }),
    (error) => {
      notify('error', `${ERRORS.saveFailed} (${describeError(error)})`);
    },
  );
}

function emptyDocument(): RoadbookDocument {
  throw new DomainError('storage/write-failed', 'Aucun document à enregistrer.');
}

/** Prend la main sur la session, après confirmation explicite. */
export async function takeOverSession(): Promise<void> {
  if (lock === null) return;
  await lock.acquire(true);
  readOnly.value = false;
  lock.start(() => {
    readOnly.value = true;
    notify('warning', ERRORS.readOnlyContext);
  });
  notify('info', 'Cette fenêtre détient désormais la session.');
}

export async function reloadSession(): Promise<void> {
  if (store === null) return;
  const snapshot = await store.load();
  if (snapshot === null) {
    notify('warning', 'Aucune session enregistrée à reprendre.');
    return;
  }
  document_.value = snapshot.document;
  parameters.value = snapshot.parameters;
  persistedSearchState.value = snapshot.searchState;
  sessionInfo.value = null;
  screen.value = 'roadbook';
}

/** Sauvegarde immédiate, au passage à l'arrière-plan (EF-MOD-008). */
export async function flushSave(): Promise<void> {
  await saver?.flush();
}

export function suspendVisibility(): void {
  lock?.stop();
  suspendSearch = true;
  void flushSave();
  void closePurgeWindow();
}

export async function resumeVisibility(): Promise<void> {
  suspendSearch = false;
  if (lock === null) return;
  const kept = await lock.refresh();
  readOnly.value = !kept;
  if (kept) {
    lock.start(() => {
      readOnly.value = true;
      notify('warning', ERRORS.readOnlyContext);
    });
  } else {
    notify('warning', ERRORS.readOnlyContext);
  }
}

// ---------------------------------------------------------------------------
// Persistance et réglages
// ---------------------------------------------------------------------------

/**
 * La persistance est demandée lors de la première modification métier
 * enregistrée localement, et non au premier lancement (EF-PLT-005).
 */
let persistenceRequested = false;

async function requestPersistenceOnce(): Promise<void> {
  if (persistenceRequested) return;
  persistenceRequested = true;
  persistence.value = await requestPersistence();
}

export async function setEndpoint(id: EndpointId): Promise<void> {
  endpointId.value = id;
  await store?.writeSetting('endpoint', id);
}

export async function setParameters(next: Parameters): Promise<void> {
  parameters.value = next;
  await store?.saveParameters(next);
}

export function setDisplay(next: DisplayParameters): void {
  void setParameters({ ...parameters.value, display: next });
}

export function toggleHiddenCategory(key: CategoryKey): void {
  const hidden = new Set(parameters.value.display.hiddenCategories);
  if (hidden.has(key)) hidden.delete(key);
  else hidden.add(key);
  setDisplay({ ...parameters.value.display, hiddenCategories: [...hidden] });
}

export function resetFilters(): void {
  setDisplay({
    ...parameters.value.display,
    hiddenCategories: [],
    pinnedOnly: false,
    excludedOnly: false,
  });
}

// ---------------------------------------------------------------------------
// Importation d'un parcours
// ---------------------------------------------------------------------------

export async function chooseGpxFile(): Promise<void> {
  const file = await pickFile('.gpx,application/gpx+xml');
  if (file === null) return;
  await importGpx(file);
}

export async function importGpx(file: File): Promise<void> {
  if (!claimOperation('importation d’un GPX')) return;
  try {
    const parsed = await parseGpxFile(file);
    if (parsed.elements.length === 0) {
      throw new DomainError(
        'gpx/no-usable-track',
        'Aucun parcours exploitable : une trace ou une route contenant au moins deux points différents est requise.',
      );
    }
    if (parsed.elements.length === 1) {
      await applyRoute(parsed, 0, file.name);
      return;
    }
    // Plusieurs traces ou routes : demander une sélection, ne rien importer
    // avant confirmation.
    trackSelection.value = {
      document: parsed,
      elements: parsed.elements.map((element) => ({
        type: element.type,
        index: element.index,
        name: element.name,
        label: element.name ?? `${element.type === 'trk' ? 'Trace' : 'Route'} ${element.index + 1}`,
        pointCount: element.count,
      })),
      fileName: file.name,
    };
  } catch (error) {
    notify('error', describeError(error));
  } finally {
    releaseOperation();
  }
}

export async function selectTrackElement(index: number): Promise<void> {
  const selection = trackSelection.value;
  if (selection === null) return;
  trackSelection.value = null;
  if (!claimOperation('importation d’un GPX')) return;
  try {
    await applyRoute(selection.document, index, selection.fileName);
  } catch (error) {
    notify('error', describeError(error));
  } finally {
    releaseOperation();
  }
}

async function applyRoute(parsed: GpxDocument, index: number, fileName: string): Promise<void> {
  const route = buildRoute(parsed, index, fileName);
  const confirmReplacement = document_.value !== null;

  const install = async (): Promise<void> => {
    await installRoute(route, fileName);
  };

  if (confirmReplacement) {
    pendingConfirmation.value = {
      title: 'Remplacer le roadbook courant ?',
      body:
        'Le parcours, les POI, la curation et les résultats courants seront remplacés. ' +
        'Vous pouvez les exporter d’abord.',
      confirmLabel: 'Remplacer',
      offerExport: true,
      onConfirm: install,
    };
    return;
  }
  await install();
}

async function installRoute(route: RouteImport, fileName: string): Promise<void> {
  const now = nowRfc3339();
  const next: RoadbookDocument = {
    id: newId(),
    title: route.title,
    createdAt: now,
    updatedAt: now,
    route: {
      sourceFileName: fileName,
      selectedElement: {
        type: route.selected.type,
        name: route.selected.name,
        index: route.selected.index,
      },
      originalPointCount: route.originalPointCount,
      track: route.track,
    },
    pois: new Map(),
    results: { ...EMPTY_RESULTS, flags: { ...EMPTY_RESULTS.flags, elevationUnavailable: !route.track.elevation.available } },
  };

  document_.value = next;
  indexCache = null;
  curveCache = null;
  fingerprintCache = null;
  persistedSearchState.value = null;
  await store?.saveSearchState(null);
  await requestPersistenceOnce();
  saver?.requestFull();
  await saver?.flush();

  for (const warning of importWarnings(route)) notify('warning', warning);
  // Le résumé du parcours importé vit dans l'onglet qui porte l'importation.
  screen.value = 'setup';
}

function importWarnings(route: RouteImport): readonly string[] {
  const warnings: string[] = [];
  if (route.warnings.ignoredRouteCount > 0) {
    warnings.push(
      `${route.warnings.ignoredRouteCount} route(s) ignorée(s) : le fichier contient des traces, qui sont prioritaires.`,
    );
  }
  if (route.warnings.segmentGapCount > 0) {
    warnings.push(
      `${route.warnings.segmentGapCount} écart(s) entre segments, le plus grand de ` +
        `${(route.warnings.largestSegmentGapMeters / 1000).toFixed(1).replace('.', ',')} km. ` +
        'La distance est comptée, le D+ ne l’est pas.',
    );
  }
  if (route.warnings.pointAnomalyCount > 0) {
    warnings.push(
      `${route.warnings.pointAnomalyCount} écart(s) supérieur(s) à 10 km entre deux points consécutifs, ` +
        `le plus grand à ${(route.warnings.largestPointAnomalyProgressionMeters / 1000).toFixed(1).replace('.', ',')} km.`,
    );
  }
  if (route.warnings.elevationUnavailable) {
    warnings.push(
      'Altitude insuffisante : les champs de D+ sont masqués et les temps sont calculés à la vitesse sur le plat.',
    );
  }
  return warnings;
}

// ---------------------------------------------------------------------------
// Recherche de POI
// ---------------------------------------------------------------------------

function searchPortion(track: ComputedTrack): { from: number; to: number } {
  const { skipStartMeters, skipFinishMeters } = parameters.value.search;
  return { from: skipStartMeters, to: Math.max(skipStartMeters, track.lengthMeters - skipFinishMeters) };
}

export function prepareSearchEstimate(zone?: ProgressionInterval): SearchEstimate | null {
  const current = document_.value;
  if (current === null) return null;
  const portion = searchPortion(current.route.track);
  const from = zone === undefined ? portion.from : Math.max(portion.from, zone.fromProgressionMeters);
  const to = zone === undefined ? portion.to : Math.min(portion.to, zone.toProgressionMeters);

  const chunks = buildChunks(simplifyTrack(current.route.track), from, to);
  const estimate = estimateSearch({
    chunks,
    categories: parameters.value.search.categories,
    corridorMeters: parameters.value.search.corridorMeters,
    fromProgressionMeters: from,
    toProgressionMeters: to,
    budgetSeconds: parameters.value.search.budgetSeconds,
  });
  searchEstimate.value = estimate;
  return estimate;
}

export function cancelSearch(): void {
  searchAbort?.abort();
}

/**
 * Lance une recherche avec les paramètres actifs. Une recherche directe depuis
 * l'écran Roadbook n'exige aucun passage préalable par un écran de paramètres.
 */
export async function startSearch(zone?: ProgressionInterval, resume = false): Promise<void> {
  const current = document_.value;
  if (current === null) return;

  if (isOffline()) {
    notify('error', ERRORS.searchOffline);
    return;
  }
  if (raceState.value.active) {
    notify('error', ERRORS.searchDuringRace);
    return;
  }

  const validation = validateParameters(parameters.value, current.route.track.lengthMeters);
  if (validation.issues.length > 0) {
    notify('error', `${ERRORS.invalidParameters} ${validation.issues.map((i) => i.message).join(' ')}`);
    return;
  }

  const portion = searchPortion(current.route.track);
  const from = zone === undefined ? portion.from : Math.max(portion.from, zone.fromProgressionMeters);
  const to = zone === undefined ? portion.to : Math.min(portion.to, zone.toProgressionMeters);
  const chunks = buildChunks(simplifyTrack(current.route.track), from, to);
  if (chunks.length === 0) {
    notify('warning', 'La portion recherchée ne contient aucun tronçon à interroger.');
    return;
  }

  const estimate = estimateSearch({
    chunks,
    categories: parameters.value.search.categories,
    corridorMeters: parameters.value.search.corridorMeters,
    fromProgressionMeters: from,
    toProgressionMeters: to,
    budgetSeconds: parameters.value.search.budgetSeconds,
  });
  searchEstimate.value = estimate;

  const launch = async (): Promise<void> => {
    await executeSearch(chunks, from, to, resume);
  };

  // Avertissement et confirmation explicite avant tout envoi.
  if (estimate.exceedsBudget || estimate.exceedsArea) {
    const detail: string[] = [];
    if (estimate.exceedsBudget) {
      detail.push(
        `Durée estimée de ${Math.round(estimate.estimatedSeconds / 60)} min, supérieure au budget de ` +
          `${Math.round(parameters.value.search.budgetSeconds / 60)} min.`,
      );
    }
    if (estimate.exceedsArea) {
      detail.push(
        `Surface interrogée d’environ ${Math.round(estimate.corridorAreaSquareMeters / 1_000_000).toLocaleString('fr-FR')} km², supérieure à 5 000 km².`,
      );
    }
    detail.push(
      `${countAutomaticPois(current.pois)} POI automatiques déjà présents seront conservés : aucun POI existant ne sera supprimé.`,
    );
    pendingConfirmation.value = {
      title: 'Confirmer le lancement de la recherche',
      body: 'La recherche dépasse un seuil d’alerte.',
      detail,
      confirmLabel: 'Lancer la recherche',
      offerExport: false,
      onConfirm: launch,
    };
    return;
  }

  await launch();
}

async function executeSearch(
  chunks: readonly SearchChunk[],
  from: number,
  to: number,
  resume: boolean,
): Promise<void> {
  const current = document_.value;
  const index = spatialIndex();
  if (current === null || index === null) return;
  if (!claimOperation('recherche de POI')) return;

  searchAbort = new AbortController();
  const parameterSetId = `ps-${newId().slice(0, 8)}`;
  const searchedAtLocal = nowLocalDateTime();

  const request = {
    chunks,
    categories: parameters.value.search.categories,
    corridorMeters: parameters.value.search.corridorMeters,
    budgetSeconds: parameters.value.search.budgetSeconds,
    endpointUrl: endpointById(endpointId.value).url,
    parameterSetId,
    searchedAtLocal,
    searchedFromMeters: from,
    searchedToMeters: to,
    computePassages: (coordinate: Coordinate, reach: number): readonly PassageComputation[] =>
      computePassages(current.route.track, index, coordinate, reach),
    inheritedAutomaticCount: countAutomaticPois(current.pois),
  };

  const state =
    resume && persistedSearchState.value !== null ? persistedSearchState.value : initialSearchState(request);

  try {
    const outcome = await runSearch(request, current.pois, state, {
      client: new OverpassClient(),
      signal: searchAbort.signal,
      shouldSuspend: () => suspendSearch,
      onProgress: (progress) => {
        searchProgress.value = progress;
      },
    });

    const integrity = mergeIntegrity(
      current.results.searchIntegrity,
      outcome.state,
      parameterSetId,
      {
        id: parameterSetId,
        categories: parameters.value.search.categories,
        corridorMeters: parameters.value.search.corridorMeters,
        skipStartMeters: parameters.value.search.skipStartMeters,
        skipFinishMeters: parameters.value.search.skipFinishMeters,
      },
    );

    const results: Results = {
      ...current.results,
      searchIntegrity: integrity,
      // Une nouvelle recherche lève le marqueur « valeurs périmées ».
      flags: { ...current.results.flags, needsRecalculation: true },
    };

    document_.value = {
      ...current,
      pois: outcome.pois,
      results,
      updatedAt: nowRfc3339(),
    };

    await requestPersistenceOnce();
    saver?.requestFull();
    await saver?.flush();

    const incomplete = outcome.state.pendingChunkIndices.length > 0 || outcome.state.missingChunkCount > 0;
    if (incomplete) {
      persistedSearchState.value = outcome.state;
      await store?.saveSearchState(outcome.state);
    } else {
      persistedSearchState.value = null;
      await store?.saveSearchState(null);
    }

    reportSearchOutcome(outcome.stoppedBy, outcome, integrity);
  } catch (error) {
    notify('error', describeError(error));
  } finally {
    searchProgress.value = null;
    searchAbort = null;
    releaseOperation();
  }
}

function mergeIntegrity(
  previous: SearchIntegrity,
  state: SearchState,
  parameterSetId: string,
  parameterSet: SearchParameterSet,
): SearchIntegrity {
  const covered = mergeIntervals([...previous.coveredRanges, ...state.coveredRanges]).map((range) => ({
    fromProgressionMeters: range.fromProgressionMeters,
    toProgressionMeters: range.toProgressionMeters,
    parameterSetId: 'parameterSetId' in range ? range.parameterSetId : parameterSetId,
    searchedAtLocal: 'searchedAtLocal' in range ? range.searchedAtLocal : nowLocalDateTime(),
  }));

  const missing = subtractIntervals(
    mergeIntervals([...previous.missingRanges, ...state.missingRanges]),
    covered,
  );

  const sets = [...previous.parameterSets];
  if (!sets.some((set) => set.id === parameterSet.id)) sets.push(parameterSet);

  return {
    complete: missing.length === 0,
    missingChunkCount: state.missingChunkCount,
    missingRanges: missing,
    coveredRanges: covered,
    parameterSets: sets,
    osmObjectsReceived: previous.osmObjectsReceived + state.osmObjectsReceived,
    poisRejectedByFilters: previous.poisRejectedByFilters + state.poisRejectedByFilters,
  };
}

function reportSearchOutcome(
  stoppedBy: string,
  outcome: { addedCount: number; mergedCount: number; state: SearchState; warningReached: boolean },
  integrity: SearchIntegrity,
): void {
  const received = outcome.state.osmObjectsReceived;
  const rejected = outcome.state.poisRejectedByFilters;
  notify(
    'info',
    `Recherche terminée : ${outcome.addedCount} POI ajoutés, ${outcome.mergedCount} mis à jour. ` +
      `${received} objets OpenStreetMap reçus, ${rejected} écartés par les filtres.`,
  );

  if (integrity.missingRanges.length > 0) {
    notify(
      'warning',
      `Résultat partiel : ${integrity.missingChunkCount} tronçon(s) manquant(s). ` +
        'Chaque zone apparaît dans la liste et peut être relancée individuellement.',
    );
  }
  if (stoppedBy === 'budget') {
    notify('warning', 'Budget de durée atteint : la recherche a été arrêtée.');
  }
  if (stoppedBy === 'cancelled') {
    notify('warning', 'Recherche annulée. Le résultat partiel obtenu est conservé.');
  }
  if (stoppedBy === 'suspended') {
    notify(
      'warning',
      'Recherche suspendue : elle peut être reprise, conservée telle quelle ou abandonnée depuis l’Accueil.',
    );
  }
  if (stoppedBy === 'cap') {
    notify(
      'error',
      `Plafond de ${RESULT_CAP.toLocaleString('fr-FR')} POI atteint : la recherche a été interrompue. ` +
        'Réduisez le corridor, la portion ou les catégories, ou lancez une purge.',
    );
  } else if (outcome.warningReached) {
    notify('warning', 'Plus de 7 000 POI automatiques : la recherche approche du plafond.');
  }
}

export async function discardInterruptedSearch(): Promise<void> {
  persistedSearchState.value = null;
  await store?.saveSearchState(null);
  sessionInfo.value = sessionInfo.value === null ? null : { ...sessionInfo.value, hasInterruptedSearch: false };
}

// ---------------------------------------------------------------------------
// Calcul
// ---------------------------------------------------------------------------

/**
 * Calcul direct depuis l'écran Roadbook. S'il échoue ou si les paramètres sont
 * invalides, aucun résultat existant n'est remplacé (EF-RBK-001, CA-026).
 */
export async function computeNow(): Promise<void> {
  const current = document_.value;
  const curve = ridingCurve();
  if (current === null || curve === null) return;

  const validation = validateParameters(parameters.value, current.route.track.lengthMeters);
  if (validation.issues.length > 0) {
    notify('error', `${ERRORS.invalidParameters} ${validation.issues.map((i) => i.message).join(' ')}`);
    // Les champs fautifs sont dans l'onglet des paramètres : on y renvoie.
    screen.value = 'setup';
    return;
  }
  for (const warning of validation.warnings) notify('warning', warning.message);

  if (!claimOperation('calcul du roadbook')) return;
  try {
    const includeSleep =
      parameters.value.schedule.sleep.enabled && !parameters.value.schedule.sleep.declinedByUser;

    const outcome = computeRoadbook({
      track: current.route.track,
      routeFingerprint: routeFingerprint(current.route.track),
      pois: current.pois,
      parameters: parameters.value,
      searchIntegrity: current.results.searchIntegrity,
      includeSleep,
      curve,
      computedAt: nowLocalDateTime(),
      mode: raceState.value.active ? 'race' : 'analysis',
    });

    document_.value = {
      ...current,
      pois: outcome.pois,
      results: { ...outcome.results, raceState: current.results.raceState },
      updatedAt: nowRfc3339(),
    };
    await requestPersistenceOnce();
    saver?.requestFull();
    await saver?.flush();
    // Le résultat est ce que l'on vient chercher : la liste passe au premier plan.
    screen.value = 'roadbook';
    closeSheet();
    notify('info', 'Roadbook calculé.');
  } catch (error) {
    notify('error', describeError(error));
  } finally {
    releaseOperation();
  }
}

// ---------------------------------------------------------------------------
// Curation — EF-POI-011
// ---------------------------------------------------------------------------

function updatePoi(id: PoiId, mutate: (poi: Poi) => Poi): void {
  const current = document_.value;
  if (current === null) return;
  if (readOnly.value) {
    notify('error', ERRORS.readOnlyContext);
    return;
  }
  const poi = current.pois.get(id);
  if (poi === undefined) return;

  const pois = new Map(current.pois);
  pois.set(id, mutate(poi));
  document_.value = { ...current, pois, updatedAt: nowRfc3339() };
  void requestPersistenceOnce();
  saver?.requestPois([id]);
}

export function setExcluded(id: PoiId, excluded: boolean): void {
  updatePoi(id, (poi) => ({ ...poi, userState: { ...poi.userState, excluded } }));
}

export function setPinned(id: PoiId, pinned: boolean): void {
  updatePoi(id, (poi) => ({ ...poi, userState: { ...poi.userState, pinned } }));
}

export function setNote(id: PoiId, note: string): void {
  const trimmed = note.normalize('NFC');
  updatePoi(id, (poi) => ({
    ...poi,
    userState: { ...poi.userState, note: trimmed.length === 0 ? null : trimmed },
  }));
}

export function setTitle(title: string): void {
  const current = document_.value;
  if (current === null) return;
  const normalized = title.normalize('NFC').trim();
  const next = normalized.length === 0 ? 'Roadbook' : [...normalized].slice(0, 100).join('');
  document_.value = { ...current, title: next, updatedAt: nowRfc3339() };
  saver?.requestFull();
}

// ---------------------------------------------------------------------------
// POI manuels — EF-POI-004
// ---------------------------------------------------------------------------

export interface ManualPoiDraft {
  readonly id: PoiId | null;
  readonly category: CategoryKey;
  readonly mode: 'coordinates' | 'progression' | 'currentPosition';
  readonly lat: number;
  readonly lon: number;
  readonly progressionKm: number;
  readonly name: string;
  readonly openingHours: string;
  readonly phone: string;
  readonly email: string;
  readonly website: string;
  readonly note: string;
}

export function saveManualPoi(draft: ManualPoiDraft): void {
  const current = document_.value;
  const index = spatialIndex();
  if (current === null || index === null) return;
  if (readOnly.value) {
    notify('error', ERRORS.readOnlyContext);
    return;
  }

  const track = current.route.track;
  const corridor = parameters.value.search.corridorMeters;

  let coordinate: Coordinate;
  let passages: readonly PassageComputation[];

  if (draft.mode === 'progression') {
    const progression = Math.min(track.lengthMeters, Math.max(0, draft.progressionKm * 1000));
    coordinate = coordinateAtProgression(track, progression);
    // Un POI créé par progression possède un passage unique, à la progression
    // saisie, avec une distance à la trace nulle.
    passages = [passageAtProgression(progression)];
  } else {
    coordinate = { lat: draft.lat, lon: draft.lon };
    passages = computePassages(track, index, coordinate, corridor);
  }

  const modelPassages: Passage[] = [...passages]
    .sort((a, b) => a.progressionMeters - b.progressionMeters)
    .map((passage, position) => ({
      index: position + 1,
      progressionMeters: passage.progressionMeters,
      distanceToRouteMeters: passage.distanceToRouteMeters,
      computed: null,
    }));

  const existing = draft.id === null ? null : (current.pois.get(draft.id) ?? null);
  const id = draft.id ?? newId();
  const name = draft.name.normalize('NFC').trim();
  const note = draft.note.normalize('NFC');

  const poi: Poi = {
    id,
    origin: 'manual',
    osm: null,
    categories: [draft.category],
    name: name.length === 0 ? null : [...name].slice(0, 200).join(''),
    openingHours: emptyToNull(draft.openingHours),
    phone: emptyToNull(draft.phone),
    email: emptyToNull(draft.email),
    website: emptyToNull(draft.website),
    accessNote: null,
    coordinate,
    passageReachMeters: corridor,
    passages: modelPassages,
    manualInput: draft.mode,
    userState: {
      excluded: existing?.userState.excluded ?? false,
      pinned: existing?.userState.pinned ?? false,
      note: note.length === 0 ? null : note,
      presenceState: 'present',
      rejectionCause: null,
      outOfSearchedPortion: false,
      categoriesChanged: false,
    },
  };

  const closest = modelPassages[0]?.distanceToRouteMeters ?? 0;
  if (draft.mode !== 'progression' && closest > corridor) {
    notify(
      'warning',
      `Ce POI est à ${Math.round(closest)} m de la trace, au-delà du corridor courant de ${corridor} m.`,
    );
  }
  const nearby = findNearbyPoi(current.pois, coordinate, poi.categories, id);
  if (nearby !== null) {
    notify('warning', `Un POI de catégorie proche existe déjà à moins de 50 m : « ${nearby} ».`);
  }

  const pois = new Map(current.pois);
  pois.set(id, poi);
  document_.value = { ...current, pois, updatedAt: nowRfc3339() };
  void requestPersistenceOnce();
  saver?.requestPois([id]);
  editingPoiId.value = null;
  closeSheet();
  // La ligne du POI enregistré s'ouvre sur son détail, dans la liste.
  screen.value = 'roadbook';
  roadbookReturnPoiId.value = id;
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function findNearbyPoi(
  pois: ReadonlyMap<PoiId, Poi>,
  coordinate: Coordinate,
  categories: readonly CategoryKey[],
  excludeId: PoiId,
): string | null {
  for (const poi of pois.values()) {
    if (poi.id === excludeId) continue;
    if (!poi.categories.some((key) => categories.includes(key))) continue;
    const deltaLat = Math.abs(poi.coordinate.lat - coordinate.lat) * 111_320;
    const deltaLon =
      Math.abs(poi.coordinate.lon - coordinate.lon) * 111_320 * Math.cos((coordinate.lat * Math.PI) / 180);
    if (Math.hypot(deltaLat, deltaLon) < 50) return poi.name ?? 'POI sans nom';
  }
  return null;
}

/**
 * Suppression définitive d'un POI manuel : elle retire réellement les données
 * du document et n'est pas annulable (EF-POI-004).
 */
export function deleteManualPoi(id: PoiId): void {
  const current = document_.value;
  if (current === null) return;
  const poi = current.pois.get(id);
  if (poi === undefined || poi.origin !== 'manual') return;

  const pois = new Map(current.pois);
  pois.delete(id);
  document_.value = { ...current, pois, updatedAt: nowRfc3339() };
  saver?.requestPois([], [id]);
  screen.value = 'roadbook';
  notify('info', 'POI manuel supprimé définitivement.');
}

// ---------------------------------------------------------------------------
// Purge — EF-POI-016
// ---------------------------------------------------------------------------

export interface PurgeCriteria {
  readonly absentOrRejected: boolean;
  readonly outOfCorridor: boolean;
  readonly outOfPortion: boolean;
  readonly disabledCategory: boolean;
}

export function purgeCandidates(criteria: PurgeCriteria): readonly Poi[] {
  const current = document_.value;
  if (current === null) return [];
  const corridor = parameters.value.search.corridorMeters;
  const active = new Set(parameters.value.search.categories);

  const candidates: Poi[] = [];
  for (const poi of current.pois.values()) {
    // Un POI manuel, épinglé, annoté ou exclu n'est jamais supprimé.
    if (poi.origin !== 'osm') continue;
    if (poi.userState.pinned || poi.userState.excluded) continue;
    if (poi.userState.note !== null) continue;
    // Un POI dont la zone n'a pas été couverte n'est jamais proposé.
    if (poi.userState.presenceState === 'rangeNotCovered') continue;

    const matches =
      (criteria.absentOrRejected &&
        (poi.userState.presenceState === 'absentFromResult' ||
          poi.userState.presenceState === 'rejectedByFilter')) ||
      (criteria.outOfCorridor && !poi.passages.some((p) => p.distanceToRouteMeters <= corridor)) ||
      (criteria.outOfPortion && poi.userState.outOfSearchedPortion) ||
      (criteria.disabledCategory && !poi.categories.some((key) => active.has(key)));

    if (matches) candidates.push(poi);
  }
  return candidates;
}

export function purgeCounts(): Readonly<Record<keyof PurgeCriteria, number>> {
  return {
    absentOrRejected: purgeCandidates({
      absentOrRejected: true,
      outOfCorridor: false,
      outOfPortion: false,
      disabledCategory: false,
    }).length,
    outOfCorridor: purgeCandidates({
      absentOrRejected: false,
      outOfCorridor: true,
      outOfPortion: false,
      disabledCategory: false,
    }).length,
    outOfPortion: purgeCandidates({
      absentOrRejected: false,
      outOfCorridor: false,
      outOfPortion: true,
      disabledCategory: false,
    }).length,
    disabledCategory: purgeCandidates({
      absentOrRejected: false,
      outOfCorridor: false,
      outOfPortion: false,
      disabledCategory: true,
    }).length,
  };
}

export async function runPurge(criteria: PurgeCriteria): Promise<void> {
  const current = document_.value;
  if (current === null) return;
  const victims = purgeCandidates(criteria);
  if (victims.length === 0) {
    notify('info', 'Aucun POI ne répond aux critères retenus.');
    return;
  }
  if (!claimOperation('purge des POI')) return;

  try {
    const pois = new Map(current.pois);
    for (const poi of victims) pois.delete(poi.id);
    document_.value = {
      ...current,
      pois,
      results: { ...current.results, flags: { ...current.results.flags, needsRecalculation: true } },
      updatedAt: nowRfc3339(),
    };
    await store?.stashPurged(victims);
    purgeUndoCount.value = victims.length;
    notify('info', `${victims.length} POI supprimés. L’annulation reste possible depuis l’écran Roadbook.`);
  } catch (error) {
    notify('error', describeError(error));
  } finally {
    releaseOperation();
  }
}

export async function undoPurge(): Promise<void> {
  const current = document_.value;
  if (current === null || store === null) return;
  const restored = await store.restorePurged();
  if (restored.length === 0) return;

  const pois = new Map(current.pois);
  for (const poi of restored) pois.set(poi.id, poi);
  document_.value = { ...current, pois, updatedAt: nowRfc3339() };
  purgeUndoCount.value = 0;
  saver?.requestFull();
  notify('info', 'Purge annulée : les POI supprimés ont été rétablis.');
}

/** Ferme la fenêtre d'annulation : la suppression devient définitive. */
export async function closePurgeWindow(): Promise<void> {
  if (purgeUndoCount.value === 0) return;
  purgeUndoCount.value = 0;
  await store?.commitPurge();
  saver?.requestFull();
}

// ---------------------------------------------------------------------------
// Import et export de fichiers
// ---------------------------------------------------------------------------

export async function exportRoadbook(): Promise<void> {
  const current = document_.value;
  if (current === null) return;
  try {
    const payload = serializeRoadbook({
      document: current,
      parameters: parameters.value,
      appVersion: APP_VERSION,
    });
    const text = JSON.stringify(payload);
    // Le document est entièrement sérialisé et validé avant toute remise.
    const check = parseRoadbookFile(JSON.parse(text) as unknown);
    if (!check.ok) throw check.error;

    const prepared = await deliverFile(proposedFileName(current.title), text);
    lastExport.value = prepared;
    await store?.writeSetting('lastExport', prepared);
    notify('info', `Export préparé : « ${prepared.fileName} ». L’enregistrement définitif appartient au navigateur.`);
  } catch (error) {
    notify('error', `Échec de préparation de l’export : ${describeError(error)}`);
  }
}

export async function chooseRoadbookFile(): Promise<void> {
  const file = await pickFile('.json,application/json');
  if (file === null) return;
  await importRoadbook(file);
}

export async function importRoadbook(file: File): Promise<void> {
  if (!claimOperation('importation d’un roadbook')) return;
  try {
    if (file.size > MAX_ROADBOOK_BYTES) {
      throw new DomainError(
        'document/too-large',
        `Le fichier dépasse la limite de 100 Mo : sa taille est de ${(file.size / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo.`,
      );
    }

    const text = await file.text();
    let payload: unknown;
    try {
      payload = JSON.parse(text) as unknown;
    } catch {
      throw new DomainError('document/not-json', 'Le fichier n’est pas un document JSON valide.');
    }

    const parsed = parseRoadbookFile(payload);
    if (!parsed.ok) throw parsed.error;

    const incoming = parsed.value;
    const current = document_.value;

    const install = async (): Promise<void> => {
      document_.value = incoming.document;
      parameters.value = incoming.parameters;
      indexCache = null;
      curveCache = null;
      fingerprintCache = null;
      persistedSearchState.value = null;
      await store?.saveSearchState(null);
      await requestPersistenceOnce();
      saver?.requestFull();
      await saver?.flush();
      screen.value = 'roadbook';
      notify('info', `Importation réussie : « ${file.name} ».`);
    };

    if (current !== null) {
      const same = current.id === incoming.document.id;
      pendingConfirmation.value = {
        title: same ? 'Autre version du même roadbook' : 'Roadbook différent',
        body:
          'Le parcours, les POI, la curation et les résultats courants seront remplacés. ' +
          'Vous pouvez les exporter d’abord.',
        detail: [
          `Document ouvert, dernière modification : ${current.updatedAt}`,
          `Fichier choisi, dernière modification : ${incoming.document.updatedAt}`,
        ],
        confirmLabel: 'Remplacer',
        offerExport: true,
        onConfirm: install,
      };
      return;
    }

    await install();
  } catch (error) {
    notify('error', describeError(error));
  } finally {
    releaseOperation();
  }
}

export async function exportParameters(): Promise<void> {
  const payload = serializeParametersFile(parameters.value, APP_VERSION);
  const prepared = await deliverFile('parametres.roadbook-parameters.json', JSON.stringify(payload));
  notify('info', `Export préparé : « ${prepared.fileName} ».`);
}

export interface ParameterImportSummary {
  readonly changes: readonly { readonly field: string; readonly before: string; readonly after: string }[];
  readonly hiddenPoiCount: number;
  readonly outOfCorridorCount: number;
  readonly next: Parameters;
}

export const parameterImportSummary: Signal<ParameterImportSummary | null> =
  signal<ParameterImportSummary | null>(null);

export async function chooseParametersFile(): Promise<void> {
  const file = await pickFile('.json,application/json');
  if (file === null) return;
  if (!claimOperation('importation de paramètres')) return;
  try {
    const payload = JSON.parse(await file.text()) as unknown;
    const parsed = parseParametersFile(payload);
    if (!parsed.ok) throw parsed.error;
    parameterImportSummary.value = summarizeParameterImport(parsed.value);
  } catch (error) {
    notify('error', describeError(error));
  } finally {
    releaseOperation();
  }
}

function summarizeParameterImport(next: Parameters): ParameterImportSummary {
  const before = parameters.value;
  const changes: { field: string; before: string; after: string }[] = [];

  const compare = (field: string, a: unknown, b: unknown): void => {
    const left = JSON.stringify(a);
    const right = JSON.stringify(b);
    if (left !== right) changes.push({ field, before: left, after: right });
  };

  compare('Catégories recherchées', before.search.categories, next.search.categories);
  compare('Corridor (m)', before.search.corridorMeters, next.search.corridorMeters);
  compare('Portion ignorée au départ (m)', before.search.skipStartMeters, next.search.skipStartMeters);
  compare('Portion ignorée à l’arrivée (m)', before.search.skipFinishMeters, next.search.skipFinishMeters);
  compare('Budget de recherche (s)', before.search.budgetSeconds, next.search.budgetSeconds);
  compare('Vitesses', before.speed, next.speed);
  compare('Départ, pauses et sommeil', before.schedule, next.schedule);
  compare('Filtres d’affichage', before.display, next.display);
  compare('Écran allumé', before.device, next.device);

  const current = document_.value;
  let hiddenPoiCount = 0;
  let outOfCorridorCount = 0;
  if (current !== null) {
    const hidden = new Set(next.display.hiddenCategories);
    for (const poi of current.pois.values()) {
      if (poi.categories.every((key) => hidden.has(key))) hiddenPoiCount += 1;
      if (!poi.passages.some((p) => p.distanceToRouteMeters <= next.search.corridorMeters)) {
        outOfCorridorCount += 1;
      }
    }
  }

  return { changes, hiddenPoiCount, outOfCorridorCount, next };
}

export async function applyParameterImport(): Promise<void> {
  const summary = parameterImportSummary.value;
  if (summary === null) return;
  parameterImportSummary.value = null;
  await setParameters(summary.next);
  notify('info', 'Paramètres remplacés. Les POI, la curation et les résultats sont inchangés.');
}

// ---------------------------------------------------------------------------
// Réinitialisation — EF-FIC-003
// ---------------------------------------------------------------------------

export async function resetParameters(): Promise<void> {
  await setParameters(defaultParameters(nowLocalDateTime()));
  notify('info', 'Paramètres réinitialisés.');
}

export async function resetRoadbook(): Promise<void> {
  await store?.resetRoadbook();
  document_.value = null;
  persistedSearchState.value = null;
  sessionInfo.value = null;
  indexCache = null;
  curveCache = null;
  fingerprintCache = null;
  expandedRows.value = new Set();
  roadbookScrollTop.value = 0;
  screen.value = 'roadbook';
  notify('info', 'Roadbook réinitialisé. Les paramètres et la disponibilité hors ligne sont conservés.');
}

export async function resetEverything(): Promise<void> {
  await store?.resetAll();
  document_.value = null;
  parameters.value = defaultParameters(nowLocalDateTime());
  endpointId.value = DEFAULT_ENDPOINT_ID;
  lastExport.value = null;
  persistedSearchState.value = null;
  sessionInfo.value = null;
  indexCache = null;
  curveCache = null;
  fingerprintCache = null;
  expandedRows.value = new Set();
  roadbookScrollTop.value = 0;
  screen.value = 'roadbook';
  notify('info', 'Réinitialisation complète effectuée. La disponibilité hors ligne est conservée.');
}

// ---------------------------------------------------------------------------
// Mise à jour de l'application — EF-PLT-004
// ---------------------------------------------------------------------------

export async function checkForUpdate(): Promise<void> {
  await offlineManager.checkForUpdate();
}

export async function activateUpdate(): Promise<void> {
  if (raceState.value.active) {
    notify('error', ERRORS.updateRefusedRace);
    return;
  }
  if (busy.value !== null) {
    notify('error', ERRORS.updateRefusedBusy);
    return;
  }
  await offlineManager.activateUpdate(async () => {
    await flushSave();
  });
}

export function confirmUpdateHealthy(): void {
  offlineManager.confirmHealthy();
}

export async function retryOfflinePreparation(): Promise<void> {
  await offlineManager.verify();
}

export function setRaceState(next: Partial<RaceUiState>): void {
  raceState.value = { ...raceState.value, ...next };
}

export function saveRaceProgress(progressionMeters: number, at: LocalDateTime): void {
  const current = document_.value;
  if (current === null) return;
  const state: RaceState = { lastProgressionMeters: progressionMeters, lastComputedAtLocal: at };
  document_.value = {
    ...current,
    results: { ...current.results, raceState: state },
  };
  saver?.requestPois([]);
  saver?.requestFull();
}
